import React, { memo } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { Feather } from "@expo/vector-icons";
import { cn } from "@/lib/utils";

export interface SuccessStateProps {
  /**
   * Bold heading (translation key).
   * Defaults to the `successState.title` i18n key.
   */
  title?: TranslationKeys;
  /**
   * Supporting description below the heading (translation key).
   * Defaults to the `successState.description` i18n key.
   */
  description?: TranslationKeys;
  /** Called when the user presses the primary action button. Omit to hide the button. */
  onPrimaryAction?: () => void;
  /**
   * Label for the primary action button (translation key).
   */
  primaryActionLabel?: TranslationKeys;
  /**
   * Custom icon node rendered above the heading.
   * When omitted a default warning symbol is shown.
   */
  icon?: React.ReactNode;
  /**
   * Variant for the primary action button.
   * Defaults to "secondary" (outlined). Pass "primary" for a solid fill button.
   */
  buttonVariant?: "primary" | "secondary" | "ghost" | "destructive";
  /** Forwarded to the root View for testing. */
  testID?: string;
  className?: string;
}

/**
 * Full-width success state — shown when a flow or operation completes successfully.
 *
 * All text props are optional and fall back to the `successState.*` i18n keys so
 * the component is usable with zero props:
 *
 * @example
 * // Zero-prop defaults
 * <SuccessState onPrimaryAction={refetch} primaryActionLabel="review.loadSuccess.goHome" />
 *
 * @example
 * // Custom copy
 * <SuccessState
 *   title="review.loadSuccess.title"
 *   description="review.loadSuccess.description"
 *   primaryActionLabel="review.loadSuccess.goHome"
 *   onPrimaryAction={goHome}
 * />
 */
export const SuccessState = memo(function SuccessState({
  title = "successState.title",
  description = "successState.description",
  onPrimaryAction,
  primaryActionLabel,
  buttonVariant = "secondary",
  className,
  icon,
  testID,
}: SuccessStateProps) {
  const { t } = useI18n();

  return (
    <View
      testID={testID}
      className={cn("flex-1 items-center justify-center px-6 py-12 gap-4", className)}
      accessibilityRole="alert"
    >
      {/* ── Icon ─────────────────────────────────────────────────────────── */}
      {icon != null ? (
        icon
      ) : (
        <View className="bg-green-5 rounded-full p-5">
          <Icon as={Feather} name="check-circle" size={40} className="text-green-50" />
        </View>
      )}

      {/* ── Text ─────────────────────────────────────────────────────────── */}
      <View className="items-center gap-2">
        <AppText weight="semibold" className="text-xl text-dark-text-50 text-center">
          {t(title)}
        </AppText>
        <AppText
          weight="regular"
          className="text-base text-supporting-text text-center leading-5 max-w-sm"
        >
          {t(description)}
        </AppText>
      </View>

      {/* ── Action button ─────────────────────────────────────────────────── */}
      {onPrimaryAction != null && (
        <Button
          label={primaryActionLabel}
          variant={buttonVariant}
          onPress={onPrimaryAction}
          testID={testID != null ? `${testID}-action` : undefined}
        />
      )}
    </View>
  );
});
